import type { SwapLesson } from "./timetableSwapWorkflow";
import { isCoverRequest, isSwapPending } from "./timetableSwapWorkflow";
import type { TTSwapRequest, TTSwapLessonSnapshot } from "./api";

/** A pending request previews both movements without changing the saved lessons. */
export function pendingSwapPlacements(
  requests: TTSwapRequest[],
  teacherIds: ReadonlySet<number>,
) {
  const slots = new Map<
    string,
    {
      requestId: number;
      lesson: TTSwapLessonSnapshot;
      destination: TTSwapLessonSnapshot;
      kind?: "SWAP" | "COVER";
    }[]
  >();
  for (const request of requests) {
    if (
      !isSwapPending(request) ||
      !request.source_snapshot ||
      !request.target_snapshot
    )
      continue;
    if (isCoverRequest(request)) {
      if (!teacherIds.has(request.target_teacher_id)) continue;
      const key = lessonSlot(
        request.source_snapshot.date,
        request.source_snapshot.period,
      );
      slots.set(key, [
        ...(slots.get(key) ?? []),
        {
          requestId: request.id,
          lesson: request.source_snapshot,
          destination: request.target_snapshot,
          kind: "COVER",
        },
      ]);
      continue;
    }
    const movements = request.chain_moves
      ? request.chain_moves.map((move) => [move.snapshot, move.destination])
      : [[request.source_snapshot, request.target_snapshot], [request.target_snapshot, request.source_snapshot]];
    for (const [lesson, destination] of movements) {
      if (!teacherIds.has(lesson.teacher_id)) continue;
      const key = lessonSlot(destination.date, destination.period);
      slots.set(key, [
        ...(slots.get(key) ?? []),
        { requestId: request.id, lesson, destination, kind: "SWAP" },
      ]);
    }
  }
  return slots;
}

export function isISODate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

export function lessonFingerprint(
  lesson: SwapLesson & { subject_id?: number },
): string {
  return JSON.stringify([
    lesson.id,
    lesson.date,
    lesson.period,
    lesson.teacher_id,
    lesson.classroom_id,
    lesson.subject_id,
    lesson.place_id,
    lesson.status,
  ]);
}

export function seoulToday(now = new Date()): string {
  return new Date(now.getTime() + 9 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

export function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function mondayOf(date: string): string {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return shiftDate(date, -(day === 0 ? 6 : day - 1));
}

export function lessonSlot(date: string, period: number): string {
  return `${date}/${period}`;
}

export type PersonalDateRange = { start: string; end: string };

export function weekRange(date: string): PersonalDateRange {
  const start = mondayOf(date);
  return { start, end: shiftDate(start, 4) };
}

export function rangeDayCount(start: string, end: string): number {
  return (
    Math.round(
      (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) /
        86_400_000,
    ) + 1
  );
}

export function personalRangeError(start: string, end: string): string {
  if (!isISODate(start) || !isISODate(end))
    return "시작일과 종료일을 선택해 주세요.";
  if (start > end) return "종료일은 시작일과 같거나 이후여야 합니다.";
  if (rangeDayCount(start, end) > 366)
    return "한 번에 최대 1년(366일)까지 선택할 수 있습니다.";
  return "";
}

// Keep dates chronological across weeks, with at most five columns per board.
// Empty weekends are folded away; weekends with lessons remain visible.
export function personalDateGroups(
  start: string,
  end: string,
  lessonDates: ReadonlySet<string> = new Set(),
) {
  if (personalRangeError(start, end)) return [];
  const all = Array.from({ length: rangeDayCount(start, end) }, (_, i) => {
    const date = shiftDate(start, i);
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
    return {
      date,
      day: ["일", "월", "화", "수", "목", "금", "토"][weekday],
      weekend: weekday === 0 || weekday === 6,
    };
  });
  const schoolDays = all.filter((d) => !d.weekend || lessonDates.has(d.date));
  const dates = schoolDays.length ? schoolDays : all;
  return Array.from({ length: Math.ceil(dates.length / 5) }, (_, i) =>
    dates.slice(i * 5, i * 5 + 5),
  );
}

// The teacher sees a free period. The actual target is the selected class's
// lesson at that time, so curriculum, teacher and room constraints stay intact.
export function personalSwapTargets<T extends SwapLesson>(
  lessons: T[],
  candidateIds: ReadonlySet<number>,
  visibleTeacherIds: ReadonlySet<number>,
): Map<string, T[]> {
  const occupied = new Set(
    lessons
      .filter(
        (s) => s.status === "SCHEDULED" && visibleTeacherIds.has(s.teacher_id),
      )
      .map((s) => lessonSlot(s.date, s.period)),
  );
  const slots = new Map<string, T[]>();
  for (const lesson of lessons) {
    const slot = lessonSlot(lesson.date, lesson.period);
    if (!candidateIds.has(lesson.id) || occupied.has(slot)) continue;
    slots.set(slot, [...(slots.get(slot) ?? []), lesson]);
  }
  return slots;
}
